import { Box, Button, Chip, Divider, TextField, Tooltip, Typography } from '@mui/material';
import { FC, memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getAllCountriesOptions } from 'redux/reducers/settings/selectors';
import { CountryWithFlag } from 'ui/CountryWithFlag';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { Duties } from 'components/Shipment/OrderDetails/OrderDetailsRow/OrderDetailsRowChild/Duties';
import { Total } from 'components/Shipment/OrderDetails/OrderDetailsRow/OrderDetailsRowChild/Total';
import {
	getHsCodeDescriptionRequest,
	getHsCodeDescriptionSuccess,
} from 'redux/reducers/shipments/reducer';
import {
	getHsCodeDescription,
	getHsCodeDescriptionIsLoad,
} from 'redux/reducers/shipments/selectors';
import {
	updateEuDdpDescriptionRequest,
	updateEuDdpHs6CodeRequest,
	updateEuDdpWeightRequest,
} from 'redux/reducers/euDdp/reducer';
import {
	getEuDdpOrder,
	getEuDdpUpdateDescriptionIsLoad,
	getEuDdpUpdateWeightIsLoad,
	getEuDdpUpdateWeightError,
} from 'redux/reducers/euDdp/selectors';
import { CustomDescriptionNotice } from 'components/Shipment/OrderDetails/OrderDetailsRow/OrderDetailsRowChild/CustomDescriptionNotice';
import { TOrderDetailsRowChildProps } from './types';

const normalizeHsCode = (code: string) => code.trim().replaceAll('.', '').replaceAll(' ', '');

export const OrderDetailsRowChild: FC<TOrderDetailsRowChildProps> = ({
	child,
	paymentCurrency,
	detalization,
}) => {
	const dispatch = useDispatch();
	const order = useSelector(getEuDdpOrder);
	const hsCodeDescriptionSelector = useSelector(getHsCodeDescription);
	const hsCodeDescriptionLoading = useSelector(getHsCodeDescriptionIsLoad);
	const customsDescriptionLoading = useSelector(getEuDdpUpdateDescriptionIsLoad);
	const weightUpdateLoading = useSelector(getEuDdpUpdateWeightIsLoad);
	const weightUpdateError = useSelector(getEuDdpUpdateWeightError);
	const countries = useSelector(getAllCountriesOptions);

	const [collapsed, setCollapsed] = useState(true);
	const [editWeight, setEditWeight] = useState(false);
	const [weightKgInput, setWeightKgInput] = useState(() =>
		child.weightInKg != null ? String(child.weightInKg) : '',
	);
	const [weightKgError, setWeightKgError] = useState('');
	// Optimistic value shown right after saving, before the server response arrives.
	const [pendingWeight, setPendingWeight] = useState<number | null>(null);

	const displayedWeight = pendingWeight != null ? pendingWeight : child.weightInKg;

	const [editCustomsDescriptions, setEditCustomsDescriptions] = useState(false);
	const [customsDescriptions, setCustomsDescriptions] = useState(child.customsDescription ?? '');
	const [customsDescriptionsError, setCustomsDescriptionsError] = useState('');
	const [hsCode, setHsCode] = useState(() => normalizeHsCode(child.hsCode ?? ''));
	const [hsCodeError, setHsCodeError] = useState('');
	const [hsCodeDescription, setHsCodeDescription] = useState(child.hsDescription ?? '');

	const requiredHsCodeLength = order?.order?.schemes?.includes('IOSS-IMPORT') ? 6 : 10;

	const handleHsCodeValidate = (code: string) => {
		const hsCodeTrim = normalizeHsCode(code);
		setHsCode(hsCodeTrim);

		const clearDescription = () => {
			dispatch(getHsCodeDescriptionSuccess(null));
			setHsCodeDescription('');
		};

		if (hsCodeTrim.length === 0) {
			setHsCodeError('HS Code is required');
			clearDescription();
			return false;
		}
		if (!/^\d+$/.test(hsCodeTrim)) {
			setHsCodeError('HS Code must contain only digits');
			clearDescription();
			return false;
		}
		// The nomenclature resolves descriptions only for even-length prefixes, so shorter
		// codes are still looked up to show the matching chapter/heading while typing.
		if (hsCodeTrim.length % 2 === 0 && hsCodeTrim.length <= 10) {
			dispatch(getHsCodeDescriptionRequest({ hsCode: hsCodeTrim }));
		} else {
			clearDescription();
		}
		if (hsCodeTrim.length !== requiredHsCodeLength) {
			setHsCodeError(`HS Code must be ${requiredHsCodeLength} digits`);
			return false;
		}
		if (hsCodeDescription === '') {
			setHsCodeError('HS Code not found');
			return false;
		}
		if (child.confirmHsCode && hsCodeTrim === normalizeHsCode(child.hsCode ?? '')) {
			setHsCodeError('HS Code need to be confirmed');
			return true;
		}

		setHsCodeError('');
		return true;
	};

	const handleUpdateHsCode = () => {
		if (order?.order.id && handleHsCodeValidate(hsCode)) {
			dispatch(
				updateEuDdpHs6CodeRequest({
					hS6Code: hsCode,
					productId: child.idProvidedByEm,
					orderId: order.order.id,
				}),
			);
		}
	};

	useEffect(() => {
		handleHsCodeValidate(hsCode);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [child.hsCode, child.hsDescription, child.confirmHsCode]);

	// The store holds a single description slot shared by every row, so a response is only
	// accepted when it belongs to the code this row is currently showing.
	useEffect(() => {
		if (normalizeHsCode(hsCodeDescriptionSelector?.hs_code ?? '') === hsCode) {
			setHsCodeDescription(hsCodeDescriptionSelector?.description ?? '');
		} else if (normalizeHsCode(child.hsCode ?? '') === hsCode) {
			setHsCodeDescription(child.hsDescription ?? '');
		}
	}, [hsCodeDescriptionSelector, hsCode, child.hsCode, child.hsDescription]);

	// The lookup is asynchronous, so "not found" can only be decided once the response for a
	// full-length code has arrived.
	useEffect(() => {
		if (hsCode.length !== requiredHsCodeLength || hsCodeDescriptionLoading) return;
		if (hsCodeDescription === '') {
			setHsCodeError('HS Code not found');
		} else if (hsCodeError === 'HS Code not found') {
			setHsCodeError('');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hsCodeDescription, hsCode, hsCodeDescriptionLoading, requiredHsCodeLength]);

	const handleCustomsDescriptionsValidate = (description: string) => {
		setCustomsDescriptions(description);
		if (description.length === 0) {
			setCustomsDescriptionsError('Customs Description is required');
			return false;
		}
		if (description.length > 150) {
			setCustomsDescriptionsError('Customs Description must be less than 150 characters');
			return false;
		}
		setCustomsDescriptionsError('');
		return true;
	};

	const handleCustomDescriptionsUpdate = (text: string) => {
		if (order?.order.id && handleCustomsDescriptionsValidate(text)) {
			dispatch(
				updateEuDdpDescriptionRequest({
					orderId: order.order.id,
					productId: child.idProvidedByEm,
					customsDescriptions: text,
				}),
			);
			setEditCustomsDescriptions(false);
		}
	};

	const resolveItemId = (): number | null => {
		if (child.itemId != null && Number.isFinite(Number(child.itemId))) {
			return Number(child.itemId);
		}
		const n = Number(String(child.id).trim());
		return Number.isFinite(n) ? n : null;
	};

	const parseWeightKgInput = (value: string) => Number(value.trim().replace(',', '.'));

	const handleWeightKgValidate = (value: string): boolean => {
		setWeightKgInput(value);
		const t = value.trim().replace(',', '.');
		if (t === '') {
			setWeightKgError('Weight is required');
			return false;
		}
		if (!/^\d+(\.\d+)?$/.test(t)) {
			setWeightKgError('Enter a valid number (kg)');
			return false;
		}
		const n = parseWeightKgInput(value);
		if (n <= 0) {
			setWeightKgError('Weight must be greater than 0');
			return false;
		}
		setWeightKgError('');
		return true;
	};

	const handleSaveWeightKg = () => {
		if (!order?.order.id) return;
		const itemId = resolveItemId();
		if (itemId == null) {
			setWeightKgError('Line item id is missing');
			return;
		}
		if (!handleWeightKgValidate(weightKgInput)) return;
		const nextWeight = parseWeightKgInput(weightKgInput);
		setPendingWeight(nextWeight);
		dispatch(
			updateEuDdpWeightRequest({
				orderId: order.order.id,
				productId: child.idProvidedByEm,
				weightInKg: nextWeight,
			}),
		);
		setEditWeight(false);
	};

	useEffect(() => {
		if (!editWeight) {
			setWeightKgInput(child.weightInKg != null ? String(child.weightInKg) : '');
			setWeightKgError('');
		}
	}, [child.weightInKg, editWeight]);

	useEffect(() => {
		if (child.weightInKg === null || child?.weightInKg == undefined || child?.weightInKg <= 0) {
			setEditWeight(true);
			setWeightKgError('Weight must be greater than 0');
		} else {
			setWeightKgError('');
		}
	}, []);

	// When the server confirms the update, the refetched value becomes the source of truth.
	useEffect(() => {
		setPendingWeight(null);
	}, [child.weightInKg]);

	// Revert the optimistic value if the update fails for this product.
	const lastHandledWeightErrorTs = useRef<number | null>(null);
	useEffect(() => {
		if (!weightUpdateError) return;
		if (lastHandledWeightErrorTs.current === weightUpdateError.ts) return;
		lastHandledWeightErrorTs.current = weightUpdateError.ts;
		if (weightUpdateError.productId === child.idProvidedByEm) {
			setPendingWeight(null);
		}
	}, [weightUpdateError, child.idProvidedByEm]);

	return (
		<Box>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-between',
					gap: 2,
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
					<Typography variant="body2">
						Product: <span style={{ fontWeight: 'bold' }}>{child.idProvidedByEm} </span>
					</Typography>
					{(() => {
						const country = countries?.find(
							(cntr) => cntr?.code === child.locationWarehouseCountry,
						);
						if (!country) return '';
						return (
							<Tooltip title="Warehouse Country">
								<Chip
									size="small"
									sx={{ p: '0 5px', ml: 1, fontSize: '.7rem', height: '20px' }}
									label={
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<CountryWithFlag countryCode={country?.code} size="small" />
										</Box>
									}
								/>
							</Tooltip>
						);
					})()}
				</Box>

				{detalization ? (
					<Box sx={{ display: 'flex', gap: 1, alignSelf: 'end' }}>
						<Typography variant="body2" sx={{ fontSize: '.8rem' }} color="text.secondary">
							<AmountAndCurrency
								amount={child?.costProvidedByEm ?? 0}
								currency={paymentCurrency}
								size={0.8}
							/>
							&nbsp;&times;&nbsp;{child.quantity}&nbsp;=&nbsp;
						</Typography>
						<AmountAndCurrency
							amount={(child?.costProvidedByEm ?? 0) * (child?.quantity ?? 0)}
							currency={paymentCurrency}
							bold
							size={0.9}
						/>
					</Box>
				) : (
					<Box>
						<AmountAndCurrency
							amount={child?.totalItemCost ?? 0}
							currency={paymentCurrency}
							bold
							size={1}
						/>
					</Box>
				)}
			</Box>
			{detalization && (
				<>
					{!!child?.customsDuty && (
						<Duties
							duty={child.customsDuty}
							nameOfDuty={`Customs Duty (${child.customDutyRate}${order?.orderDetails.rows[0].description === 'IOSS-IMPORT' ? ' EUR/HS Line' : '%'})`}
							paymentCurrency={paymentCurrency}
						/>
					)}
					<Duties
						duty={child.itemOnlyVat}
						nameOfDuty={`Item VAT (${child.vatRate}%)`}
						paymentCurrency={paymentCurrency}
					/>
					{!!child.proratedDeliveryCost && (
						<Duties
							duty={child.proratedDeliveryCost}
							nameOfDuty="Delivery Charge"
							paymentCurrency={paymentCurrency}
						/>
					)}
					{!!child.itemDeliveryChargeVat && (
						<Duties
							duty={child.itemDeliveryChargeVat}
							nameOfDuty={`VAT on Delivery Charge (${child.vatRate}%)`}
							paymentCurrency={paymentCurrency}
						/>
					)}
					{!!child.proratedEasFee && (
						<Duties
							duty={child.proratedEasFee}
							nameOfDuty="EAS Fee"
							paymentCurrency={paymentCurrency}
						/>
					)}
					{!!child.proratedEasFeeVat && (
						<Duties
							duty={child.proratedEasFeeVat}
							nameOfDuty="VAT on EAS Fee (22%)"
							paymentCurrency={paymentCurrency}
						/>
					)}

					<Divider sx={{ mt: 0.4, mb: 0.4, ml: 4 }} />
					<Total
						totalPerItem={child?.costProvidedByEm ?? 0}
						quantity={child.quantity}
						totalCost={child.totalItemCost}
						paymentCurrency={paymentCurrency}
					/>
				</>
			)}
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'start',
					alignItems: 'start',
					gap: 0,
					ml: 4,
					mb: 1,
				}}
			>
				<Typography variant="body2" sx={{ fontSize: '.8rem' }}>
					<span>Product Description: </span>
					<span style={{ fontWeight: 'bold' }}>{child.description}</span>
				</Typography>
			</Box>

			{!editCustomsDescriptions && !!child.customsDescription && (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'start',
						alignItems: 'start',
						gap: 0,
						ml: 4,
						mb: 1,
					}}
				>
					<Typography variant="body2" sx={{ fontSize: '.8rem' }}>
						Customs Description:
						<span style={{ fontWeight: 'bold' }}> {child.customsDescription ?? '—'}</span>
					</Typography>
					{order?.order?.valuesStatus === 'Unprocessed' && (
						<Button
							size="small"
							variant="text"
							color="primary"
							type="button"
							sx={{ p: 0, m: 0 }}
							onClick={() => {
								setEditCustomsDescriptions(true);
							}}
						>
							Edit Customs Description
						</Button>
					)}
				</Box>
			)}
			{order?.order?.valuesStatus === 'Unprocessed' &&
				!editCustomsDescriptions &&
				!child.customsDescription && (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'start',
							alignItems: 'center',
							gap: 1,
							ml: 4,
							mb: 1,
						}}
					>
						<Typography
							variant="body2"
							color="error"
							sx={{ fontSize: '.8rem', fontWeight: 'bold' }}
						>
							Customs description is not available. Please add it.
						</Typography>
						<Button
							size="small"
							variant="contained"
							color="primary"
							type="button"
							sx={{ p: '0 10px', m: 0, fontSize: '.7rem' }}
							onClick={() => {
								handleCustomDescriptionsUpdate(child.description);
							}}
						>
							Use the Product Description as a Customs Description
						</Button>

						<Button
							size="small"
							variant="contained"
							color="primary"
							type="button"
							sx={{ p: '0 10px', m: 0, fontSize: '.7rem' }}
							onClick={() => {
								setEditCustomsDescriptions(true);
							}}
						>
							Add Customs Description
						</Button>
					</Box>
				)}
			{editCustomsDescriptions && (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 0,
						ml: 4,
						mb: 2,
					}}
				>
					<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
						<TextField
							size="small"
							value={customsDescriptions}
							fullWidth
							multiline
							rows={6}
							InputLabelProps={{
								style: {
									fontSize: '.8rem',
									marginTop: '1px',
								},
							}}
							sx={{
								mt: '10px',
								'& .MuiInputBase-root': {
									fontSize: '.9rem',
								},
								'& .MuiFormHelperText-root': {
									fontSize: '.6rem',
									lineHeight: '.6rem!important',
								},
								'& legend': {
									fontSize: '.6rem',
								},
							}}
							label="Customs Description"
							onChange={(e) => handleCustomsDescriptionsValidate(e.target.value)}
							error={!!customsDescriptionsError}
							helperText={customsDescriptionsError}
						/>
						<CustomDescriptionNotice />
					</Box>
					<Button
						size="small"
						variant="contained"
						color="primary"
						type="button"
						sx={{
							maxHeight: '24px',
							marginLeft: '4px',
							width: '200px',
							marginTop: '5px',
						}}
						disabled={
							!!customsDescriptionsError ||
							customsDescriptionLoading ||
							customsDescriptions === child.customsDescription
						}
						onClick={() => {
							handleCustomDescriptionsUpdate(customsDescriptions);
						}}
					>
						Save Customs Description
					</Button>
				</Box>
			)}

			{order?.order?.valuesStatus !== 'Unprocessed' && (
				<Box sx={{ ml: 4, mb: 1 }}>
					<Typography variant="body2" sx={{ fontSize: '.8rem' }}>
						Weight (kg):
						<span style={{ fontWeight: 'bold' }}>
							{' '}
							{displayedWeight != null ? displayedWeight : '—'}
						</span>
					</Typography>
				</Box>
			)}
			{order?.order?.valuesStatus === 'Unprocessed' && !editWeight && displayedWeight != null && (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'start',
						alignItems: 'start',
						gap: 0,
						ml: 4,
						mb: 1,
					}}
				>
					<Typography variant="body2" sx={{ fontSize: '.8rem' }}>
						Weight (kg):
						<span style={{ fontWeight: 'bold' }}> {displayedWeight}</span>
					</Typography>
					<Button
						size="small"
						variant="text"
						color="primary"
						type="button"
						sx={{ p: 0, m: 0 }}
						onClick={() => {
							setWeightKgInput(String(displayedWeight ?? ''));
							setEditWeight(true);
							setWeightKgError('');
						}}
						disabled={resolveItemId() == null}
					>
						Edit Weight
					</Button>
				</Box>
			)}
			{order?.order?.valuesStatus === 'Unprocessed' && !editWeight && displayedWeight == null && (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'start',
						alignItems: 'center',
						gap: 1,
						flexWrap: 'wrap',
						ml: 4,
						mb: 1,
					}}
				>
					<Typography variant="body2" color="error" sx={{ fontSize: '.8rem', fontWeight: 'bold' }}>
						Weight (kg) is not set for this item.
					</Typography>
					<Button
						size="small"
						variant="contained"
						color="primary"
						type="button"
						sx={{ p: '0 10px', m: 0, fontSize: '.7rem' }}
						disabled={resolveItemId() == null}
						onClick={() => {
							setWeightKgInput('');
							setEditWeight(true);
							setWeightKgError('');
						}}
					>
						Add Weight
					</Button>
					{resolveItemId() == null && (
						<Typography variant="caption" color="text.secondary">
							Line item id is missing — refresh the page or contact support.
						</Typography>
					)}
				</Box>
			)}
			{order?.order?.valuesStatus === 'Unprocessed' && editWeight && (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 0,
						ml: 4,
						mb: 2,
						maxWidth: 200,
					}}
				>
					<TextField
						size="small"
						value={weightKgInput}
						InputLabelProps={{
							style: {
								fontSize: '.8rem',
								marginTop: '1px',
							},
						}}
						sx={{
							mt: '10px',
							'& .MuiInputBase-root': {
								minWidth: '200px',
								maxHeight: '30px',
							},
							'& .MuiFormHelperText-root': {
								fontSize: '.6rem',
								lineHeight: '.6rem!important',
							},
							'& .MuiFormLabel-root': {
								lineHeight: '.8rem!important',
							},
							'& legend': {
								fontSize: '.6rem',
							},
						}}
						label="Weight (kg)"
						inputProps={{
							inputMode: 'decimal',
						}}
						onChange={(e) => handleWeightKgValidate(e.target.value)}
						error={!!weightKgError}
						helperText={
							weightKgError || (resolveItemId() == null ? 'Line item id is missing' : undefined)
						}
					/>
					<Button
						size="small"
						variant="contained"
						color="primary"
						type="button"
						sx={{
							maxHeight: '24px',
							marginLeft: '4px',
							width: '200px',
							marginTop: '5px',
						}}
						disabled={
							!!weightKgError ||
							weightUpdateLoading ||
							weightKgInput.trim() === '' ||
							resolveItemId() == null ||
							(child.weightInKg != null &&
								/^\d+(\.\d+)?$/.test(weightKgInput.trim().replace(',', '.')) &&
								Math.abs(Number(child.weightInKg) - parseWeightKgInput(weightKgInput)) < 1e-9)
						}
						onClick={handleSaveWeightKg}
					>
						Save Weight
					</Button>
				</Box>
			)}

			<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, ml: 4 }}>
				<Box sx={{ width: '200px' }}>
					{order?.order?.valuesStatus === 'Unprocessed' ? (
						<>
							<TextField
								size="small"
								value={hsCode}
								InputLabelProps={{
									style: {
										fontSize: '.8rem',
										marginTop: '1px',
									},
								}}
								sx={{
									mt: '10px',
									'& .MuiInputBase-root': {
										minWidth: '200px',
										maxHeight: '30px',
									},
									'& .MuiFormHelperText-root': {
										fontSize: '.6rem',
										lineHeight: '.6rem!important',
									},
									'& .MuiFormLabel-root': {
										lineHeight: '.8rem!important',
									},
									'& legend': {
										fontSize: '.6rem',
									},
								}}
								label="HS Code"
								onChange={(e) => handleHsCodeValidate(e.target.value)}
								error={!!hsCodeError}
								helperText={hsCodeError}
							/>
							<Button
								size="small"
								variant="contained"
								color="primary"
								type="button"
								sx={{
									borderRadius: '12px!important',
									minWidth: '200px',
									textTransform: 'none',
									maxHeight: '24px',
								}}
								disabled={
									hsCodeDescription === '' ||
									hsCodeDescriptionLoading ||
									hsCode.length !== requiredHsCodeLength ||
									(hsCode === normalizeHsCode(child.hsCode ?? '') && !child.confirmHsCode)
								}
								onClick={handleUpdateHsCode}
							>
								{!child.hsCode
									? 'Add HS Code'
									: hsCode === normalizeHsCode(child.hsCode ?? '') && child.confirmHsCode
										? `Confirm HS Code`
										: `Update HS Code`}
							</Button>
						</>
					) : (
						<Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
							<Typography variant="body2" sx={{ fontSize: '.8rem' }}>
								HS Code:
							</Typography>
							<Typography variant="h5" sx={{ fontSize: '.9rem', fontWeight: 'bold' }}>
								{child.hsCode || '—'}
							</Typography>
						</Box>
					)}
				</Box>
				<Box sx={{ width: '100%' }}>
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{
							maxHeight: collapsed ? '40px' : '100%',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							p: 0,
							mt: order?.order?.valuesStatus === 'Unprocessed' ? '16px' : 0,
						}}
					>
						{order?.order?.valuesStatus === 'Unprocessed' && hsCodeDescription === '' ? (
							<span>
								Please visit{' '}
								<Link
									to="https://easproject.com/eu-customs-tariffs-guide/"
									target="_blank"
									rel="noreferrer"
									style={{ color: '#016193' }}
								>
									EAS EU Customs Tariffs Guide
								</Link>{' '}
								to find applicable customs code.
							</span>
						) : order?.order?.valuesStatus === 'Unprocessed' ? (
							hsCodeDescription
						) : (
							child.hsDescription
						)}
					</Typography>
					{(hsCodeDescription?.length ?? 0) > 200 && (
						<Button
							size="small"
							variant="text"
							color="primary"
							type="button"
							sx={{
								textTransform: 'none',
								fontSize: '.8rem',
								lineHeight: '.1rem',
								p: 0,
								mt: '-16px',
								fontWeight: 'bold',
							}}
							onClick={() => setCollapsed(!collapsed)}
						>
							{collapsed ? 'Show more' : 'Show less'}
						</Button>
					)}
				</Box>
			</Box>
		</Box>
	);
};

export const OrderDetailsRowChildMemo = memo(OrderDetailsRowChild);
